import React, { useEffect, useRef, useState } from 'react';
import { Mic, MicOff, X } from 'lucide-react';

/**
 * Phase 4 — Voice command overlay.
 * Uses the browser's Web Speech API. Parses simple verbs into actions.
 * Auto-stops after 6s of silence.
 *
 * Props:
 *   onCommand({ type, raw }) where type is one of:
 *     'accept' | 'decline' | 'call' | 'message' | 'arrived' | 'start_trip'
 *     'end_trip' | 'go_offline' | 'go_online' | 'report' | 'unknown'
 *   onClose()
 */
const COMMAND_PHRASES = [
  { type: 'accept', match: /\b(accept|take|grab|pick(ing)?\s*up)\b/i },
  { type: 'decline', match: /\b(decline|reject|skip|no\s*thanks)\b/i },
  { type: 'call', match: /\b(call|phone|ring)\b.*\b(passenger|rider|client)\b|\b(call|phone)\s*passenger\b|\bcall\b/i },
  { type: 'message', match: /\b(message|text|sms)\b/i },
  { type: 'arrived', match: /\b(i('?ve|\s*have)?\s*arrived|i'?m\s*here|reached|arrived)\b/i },
  { type: 'start_trip', match: /\b(start\s*(the\s*)?trip|begin\s*trip|let'?s\s*go)\b/i },
  { type: 'end_trip', match: /\b(end\s*(the\s*)?trip|finish\s*trip|drop(\s*off)?\s*complete|complete\s*trip)\b/i },
  { type: 'go_offline', match: /\b(go\s*offline|stop\s*working|end\s*shift|i'?m\s*done)\b/i },
  { type: 'go_online', match: /\b(go\s*online|start\s*shift|i'?m\s*ready)\b/i },
  { type: 'report', match: /\b(report\s*(an?\s*)?issue|problem|emergency|sos)\b/i },
];

const parseCommand = (raw) => {
  if (!raw) return { type: 'unknown', raw };
  for (const c of COMMAND_PHRASES) {
    if (c.match.test(raw)) return { type: c.type, raw };
  }
  return { type: 'unknown', raw };
};

const VoiceCommandOverlay = ({ onCommand, onClose }) => {
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [resultLabel, setResultLabel] = useState(null);
  const [supported, setSupported] = useState(true);
  const recogRef = useRef(null);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setSupported(false);
      return undefined;
    }
    const r = new SR();
    r.continuous = false;
    r.interimResults = true;
    r.lang = 'en-US';
    r.maxAlternatives = 1;
    r.onresult = (ev) => {
      let final = '';
      let interim = '';
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const t = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) final += t;
        else interim += t;
      }
      const text = (final || interim).trim();
      setTranscript(text);
      if (final) {
        const cmd = parseCommand(final);
        setResultLabel(cmd.type === 'unknown' ? `“${final.trim()}”` : `✓ ${cmd.type.replace('_', ' ')}`);
        setListening(false);
        // Send the command up — small delay so the user sees the result label
        setTimeout(() => {
          onCommand?.(cmd);
          onClose?.();
        }, 600);
      }
    };
    r.onerror = () => { setListening(false); };
    r.onend = () => { setListening(false); };
    recogRef.current = r;
    return () => {
      try { r.abort(); } catch (_) { /* noop */ }
    };
  }, [onCommand, onClose]);

  const start = () => {
    if (!recogRef.current) return;
    setTranscript('');
    setResultLabel(null);
    try { recogRef.current.start(); setListening(true); } catch (_) { /* already started */ }
  };

  const stop = () => {
    if (!recogRef.current) return;
    try { recogRef.current.stop(); } catch (_) { /* noop */ }
    setListening(false);
  };

  // Auto-start on mount when supported.
  useEffect(() => {
    if (supported) start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supported]);

  return (
    <div className="fixed inset-0 z-[85] bg-black/75 backdrop-blur-sm flex items-end" onClick={onClose} data-testid="voice-overlay-v2">
      <div
        className="w-full bg-gray-900 text-white rounded-t-3xl p-6 pb-8 animate-slide-up"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: 'calc(32px + env(safe-area-inset-bottom, 0))' }}
      >
        <div className="flex justify-between items-center mb-4">
          <p className="text-[12px] uppercase tracking-wider font-semibold text-gray-400">Voice command</p>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="voice-close" className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center hover:bg-gray-700">
            <X className="w-4 h-4 text-gray-300" />
          </button>
        </div>

        {!supported ? (
          <p className="py-12 text-center text-sm text-gray-300" data-testid="voice-unsupported">
            Voice commands aren't available on this device. Please use the buttons.
          </p>
        ) : (
          <>
            <div className="flex items-center justify-center my-4">
              <button
                type="button"
                onClick={listening ? stop : start}
                data-testid="voice-mic-toggle"
                aria-label={listening ? 'Stop listening' : 'Start listening'}
                className={`w-24 h-24 rounded-full flex items-center justify-center transition-all ${
                  listening
                    ? 'bg-emerald-500 ring-8 ring-emerald-500/25 animate-pulse'
                    : 'bg-emerald-600 hover:bg-emerald-700'
                }`}
              >
                {listening ? <Mic className="w-10 h-10 text-white" /> : <MicOff className="w-10 h-10 text-white" />}
              </button>
            </div>

            <p className="text-center text-[14px] text-gray-200 min-h-[24px]" data-testid="voice-transcript">
              {transcript || (listening ? 'Listening…' : 'Tap the mic to start')}
            </p>
            {resultLabel && (
              <p className="text-center text-[12px] text-emerald-300 mt-1" data-testid="voice-result">{resultLabel}</p>
            )}

            <p className="text-center text-[11px] uppercase tracking-wider font-semibold text-gray-500 mt-5 mb-2">Try saying</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {['Accept ride', 'Call passenger', 'I have arrived', 'Go offline', 'End trip', 'Report issue'].map((c) => (
                <span key={c} className="px-3 py-1.5 rounded-full bg-gray-800 text-[12px] text-gray-200">{c}</span>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export { parseCommand };
export default VoiceCommandOverlay;
